import { CommonModule } from '@angular/common';
import { NgModule } from '@angular/core';
import { ROUTES, RouterModule, Routes } from '@angular/router';
import { MsalGuard } from '@azure/msal-angular';
import { BrowserUtils } from '@azure/msal-browser';

import { environment } from 'src/environments/environment';

import { NotAllowedComponent } from './pages/not-allowed/not-allowed.component';
import { NotFoundComponent } from './pages/not-found/not-found.component';
import { ProfileComponent } from './pages/profile/profile.component';

import { HomeModule } from './modules/home/home.module';
import { CommunityMapComponent } from './modules/scope-community/community-map/community-map.component';
import { agentsResolver } from './modules/scope-community/community-map/resolver/agents.resolver';
import { geoLocationResolver } from './modules/scope-community/community-map/resolver/geo-locations.resolver';
import { StyleGuideComponent } from './pages/styleguide/styleguide.component';
import { customTitleResolver } from './shared/resolvers/customTitle.resolver';

/** default routes of the app */
const standardRoutes: Routes = [
    {
        path: '',
        pathMatch: 'full',
        component: HomeModule
    },
    {
        path: 'profile',
        component: ProfileComponent,
        canActivate: [MsalGuard],
        title: customTitleResolver,
        data: {
            breadcrumb: $localize`:@@page-breadcrumb-profile:User Profile`
        }
    },
    {
        path: '403',
        component: NotAllowedComponent,
        title: customTitleResolver,
        data: {
            title: $localize`:@@page-title-403:Not allowed`,
            breadcrumb: $localize`:@@page-breadcrumb-403:Not allowed`
        }
    },
    {
        path: '404',
        component: NotFoundComponent,
        title: customTitleResolver,
        data: {
            title: $localize`:@@page-title-404:Page not found`,
            breadcrumb: $localize`:@@page-breadcrumb-404:Page not found`
        }
    },
    {
        path: '**',
        component: NotFoundComponent,
        title: customTitleResolver,
        data: {
            title: $localize`:@@page-title-404:Page not found`,
            breadcrumb: $localize`:@@page-breadcrumb-404:Page not found`
        }
    }
];

@NgModule( {
    imports: [
        CommonModule,
        RouterModule.forRoot( [], {
            anchorScrolling: 'enabled',
            bindToComponentInputs: true,
            onSameUrlNavigation: 'reload',
            initialNavigation:
                !BrowserUtils.isInIframe() && !BrowserUtils.isInPopup() ? 'enabledNonBlocking' : 'disabled'
        } )
    ],
    exports: [RouterModule],
    providers: [
        {
            provide: ROUTES,
            useFactory: () => {
                const dynamicRoutes: Routes = [];

                const tenantsRoute = {
                    path: 'tenants',
                    canActivate: [MsalGuard],
                    loadChildren: () => import( './modules/tenants/tenants.module' ).then( ( m ) => m.TenantsModule ),
                    title: customTitleResolver,
                    data: {
                        title: $localize`:@@page-title-tenants:Tenants`,
                        breadcrumb: $localize`:@@page-breadcrumb-tenants:Tenants`
                    }
                };
                dynamicRoutes.push( tenantsRoute );

                const instancesRoute = {
                    path: 'instances',
                    canActivate: [MsalGuard],
                    loadChildren: () => import( './modules/instances/instances.module' ).then( ( m ) => m.InstancesModule ),
                    title: customTitleResolver,
                    data: {
                        title: $localize`:@@page-title-instances:Instances`,
                        breadcrumb: $localize`:@@page-breadcrumb-instances:Instances`
                    }
                };
                dynamicRoutes.push( instancesRoute );

                if ( environment.optionalModules.showCommunityMap && !environment.optionalModules.showScopeCommunity ) {
                    const route = {
                        path: 'community-map',
                        component: CommunityMapComponent,
                        resolve: {
                            agents: agentsResolver,
                            geoLocations: geoLocationResolver
                        },
                        title: customTitleResolver,
                        data: {
                            title: $localize`:@@page-title-community-map:Community Map`,
                            breadcrumb: $localize`:@@page-breadcrumb-community-map:Community Map`
                        }
                    };
                    dynamicRoutes.push( route );
                }

                if ( environment.optionalModules.showScopeCommunity ) {
                    const route = {
                        path: 'scope-community',
                        loadChildren: () => import( './modules/scope-community/scope-community.module' ).then( ( m ) => m.ScopeCommunityModule ),
                        title: customTitleResolver,
                        data: {
                            title: $localize`:@@page-title-scope-community:Scope Community`,
                            breadcrumb: $localize`:@@page-breadcrumb-scope-community:Scope Community`
                        }
                    };
                    dynamicRoutes.push( route );
                }

                if ( environment.optionalModules.showUnLocodes ) {
                    const route = {
                        path: 'unlocodes',
                        loadChildren: () =>
                            import( './modules/unlocodes/unlocodes.module' ).then( ( m ) => m.UnlocodesModule ),
                        title: customTitleResolver,
                        data: {
                            title: $localize`:@@page-title-unlocodes:UN/LOCODEs`,
                            breadcrumb: $localize`:@@page-breadcrumb-unlocodes:UN/LOCODEs`
                        }
                    };
                    dynamicRoutes.push( route );
                }

                if ( environment.optionalModules.showUserAdmin ) {
                    const route = {
                        path: 'administration',
                        canActivate: [MsalGuard],
                        loadChildren: () =>
                            import( './modules/administration/administration.module' ).then(
                                ( m ) => m.AdministrationModule
                            ),
                        title: customTitleResolver,
                        data: {
                            title: $localize`:@@page-title-administration:Administration`,
                            breadcrumb: $localize`:@@page-breadcrumb-administration:Administration`
                        }
                    };
                    dynamicRoutes.push( route );
                }

                if ( environment.optionalModules.showOrganization ) {
                    const route = {
                        path: 'organization',
                        canActivate: [MsalGuard],
                        loadChildren: () =>
                            import( './modules/organization/organization.module' ).then( ( m ) => m.OrganizationModule ),
                        title: customTitleResolver,
                        data: {
                            title: $localize`:@@page-title-organization:Organization`,
                            breadcrumb: $localize`:@@page-breadcrumb-organization:Organization`
                        }
                    };
                    dynamicRoutes.push( route );
                }

                if ( environment.optionalModules.showStyleGuide ) {
                    const route = {
                        path: 'styleguide',
                        component: StyleGuideComponent,
                        title: customTitleResolver,
                        data: {
                            title: 'StyleGuide',
                            breadcrumb: 'StyleGuide'
                        }
                    };
                    dynamicRoutes.push( route );
                }

                // NOTE: dynamic routes need to be handled first
                const routes = [...dynamicRoutes, ...standardRoutes];
                return routes;
            },
            multi: true
        }
    ]
} )
export class AppRoutingModule { }
